import React from 'react';
import { StaticQuery, graphql } from 'gatsby';
import { withPreview } from 'gatsby-source-prismic-graphql';

import {
  Prismic_HomepageConnectionConnection,
  Prismic_HomepageBodyNavigation_Groups,
  Prismic_HomepageBodyNavigation_GroupsFields,
} from 'types/prismic';
import { PrismicBaseDocument } from 'types/prismicBaseDocument';

import { linkableFragment } from 'graphql/linkableFragment';
import { pageFragment } from 'graphql/pageFragment';
import { articleFragment } from 'graphql/articleFragment';
import { projectFragment } from 'graphql/projectFragment';
import { metaFragment } from 'graphql/metaFragment';
import { associationFragment } from 'graphql/associationFragment';

import { linkResolver } from 'utils/linkResolver';

import { Nav } from 'components/nav/Nav';
import { Link } from 'components/link/Link';
import { asText, RichText } from 'components/rich-text/RichText';
import { NavSection } from 'components/nav/NavSection';
import { NavSecondary } from 'components/nav/NavSecondary';
import { useIntl } from 'react-intl';

interface INavigationProps {
  prismic: { allHomepages: Prismic_HomepageConnectionConnection };
}

const Navigation = ({ prismic }: INavigationProps) => {
  const homepages = prismic?.allHomepages?.edges;

  const { locale } = useIntl();

  const findHomepage = homepages?.find(i => i?.node?._meta?.lang === locale) ?? null;

  if (!findHomepage) {
    return null;
  }

  const homepage = findHomepage.node;

  const nav = homepage?.body ?? [];
  const info = homepage?.footer_info_text;
  const list = (homepage?.footer_links ?? []).map(i => ({
    title: asText(i.link_title),
    link: linkResolver(i.link_link),
  }));

  return (
    <Nav secondary={<NavSecondary info={<RichText>{info}</RichText>} list={list} />}>
      {nav.map((item: Prismic_HomepageBodyNavigation_Groups, i: number) => (
        <NavSection
          key={i}
          heading={asText(item?.primary?.title)}
          link={linkResolver(item?.primary?.default_link ?? null)}
        >
          {(item?.fields ?? []).map(
            (field: Prismic_HomepageBodyNavigation_GroupsFields, j: number) => (
              <Link to={linkResolver(field.link)} key={j}>
                {asText(field?.title ?? []) ||
                  asText((field?.link as PrismicBaseDocument)?.title ?? [])}
              </Link>
            )
          )}
        </NavSection>
      ))}
    </Nav>
  );
};

export const NavContainer = () => {
  // This query is also in gatsby-node
  const navQuery = graphql`
    query NavQ {
      prismic {
        allHomepages {
          edges {
            node {
              _meta {
                lang
              }
              footer_info_text
              footer_links {
                link_title
                link_link {
                  ...linkable
                }
              }
              body {
                ... on PRISMIC_HomepageBodyNavigation_groups {
                  primary {
                    title
                    url
                    default_link {
                      ...linkable
                    }
                  }
                  fields {
                    title
                    link {
                      ...linkable
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  return (
    <StaticQuery
      query={navQuery}
      render={withPreview(Navigation, navQuery, [
        linkableFragment,
        pageFragment,
        articleFragment,
        projectFragment,
        metaFragment,
        associationFragment,
      ])}
    />
  );
};
